import { computed } from '@angular/core';
import { signalStore, withState, withComputed, patchState, withMethods } from '@ngrx/signals';

/** State shape for tenant branding data available on the pre-auth screens. */
export interface BrandingState {
  /** The tenant's brand name text, or null when not yet loaded / unavailable. */
  logoText: string | null;
  /** Short-lived SAS URL for the tenant's logo image, or null when not yet loaded / unavailable. */
  logoUrl: string | null;
  /**
   * When true, the username/password login form is hidden on the login screen and only the SSO
   * options are shown. Sourced from the customer validation response; defaults to false.
   */
  isLoginFormDisabled: boolean;
}

/**
 * Shared signal store that holds tenant branding data used across all
 * pre-auth screens (login, forgot-password, etc.).
 * The store is populated during the customer-identification flow and read
 * by page components to supply inputs to BrandLogoComponent and TenantLogoComponent.
 */
export const BrandingStore = signalStore(
  { providedIn: 'root' },
  withState<BrandingState>({
    logoText: null,
    logoUrl: null,
    isLoginFormDisabled: false,
  }),
  withComputed(({ logoText }) => ({
    /** True when a non-empty logo text has been loaded. */
    hasLogoText: computed(() => logoText() !== null && logoText() !== ''),
  })),
  withMethods((store) => ({
    /**
     * Sets the tenant logo text after the customer-identification step completes.
     * @param text The brand name string, or null to clear it.
     */
    setLogoText(text: string | null): void {
      patchState(store, { logoText: text });
    },

    /**
     * Sets the tenant logo SAS URL after the customer-identification step completes.
     * The URL is held in-memory only and never persisted to browser storage.
     * @param url The short-lived SAS URL, or null when no logo is configured.
     */
    setLogoUrl(url: string | null): void {
      patchState(store, { logoUrl: url });
    },

    /**
     * Sets whether the username/password login form is disabled for the current customer.
     * @param disabled True to hide the login form and show only SSO options.
     */
    setIsLoginFormDisabled(disabled: boolean): void {
      patchState(store, { isLoginFormDisabled: disabled });
    },
  }))
);
